import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { accessAPI, authAPI } from '@/lib/api';
import { toast } from 'sonner';
import { BrutalCard, BrutalButton } from '@/components/brutal';
import { Copy, KeyRound } from 'lucide-react';

const MODES = [
  { id: 'invite_only', label: 'Invite only', desc: 'New accounts need an invite code you issued and must accept the pilot terms. This is the safe setting while the patent filings are in progress.' },
  { id: 'closed', label: 'Closed', desc: 'No new accounts at all, even with a code. Existing accounts keep working.' },
  { id: 'open', label: 'Open', desc: 'Anyone can register. This makes Semantic Vision publicly available.' },
];

const inputCls = 'border-2 border-black px-3 py-2 text-sm w-full';
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');

const ChangePassword = () => {
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await authAPI.changePassword(cur, next);
      toast.success('Password changed');
      setCur(''); setNext('');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not change the password'); }
    setBusy(false);
  };
  return (
    <BrutalCard className="p-5">
      <h3 className="text-xl font-black uppercase mb-3">Change my password</h3>
      <form onSubmit={save} className="grid md:grid-cols-3 gap-3 items-end" data-testid="change-password">
        <label className="text-xs font-bold uppercase">Current password<input type="password" required className={inputCls} value={cur} onChange={(e) => setCur(e.target.value)} autoComplete="current-password" /></label>
        <label className="text-xs font-bold uppercase">New password (10+ characters)<input type="password" required minLength={10} className={inputCls} value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" /></label>
        <BrutalButton variant="amber" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Change password'}</BrutalButton>
      </form>
    </BrutalCard>
  );
};

const AccessTab = () => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['admin-access'], queryFn: async () => (await accessAPI.adminGet()).data });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [ack, setAck] = useState(false);
  const [form, setForm] = useState({ note: '', max_uses: 1, email: '', expires_in_days: '' });
  const [busy, setBusy] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ['admin-access'] }).then(() => qc.invalidateQueries({ queryKey: ['public-access'] }));

  const setMode = async (mode, confirmPublic = false) => {
    setBusy(true);
    try {
      await accessAPI.setMode(mode, confirmPublic);
      toast.success(`Sign-up is now ${mode.replace('_', ' ')}`);
      setConfirmOpen(false); setAck(false);
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not change the setting'); }
    setBusy(false);
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { note: form.note, max_uses: Number(form.max_uses) || 1 };
      if (form.email.trim()) payload.email = form.email.trim();
      if (form.expires_in_days) payload.expires_in_days = Number(form.expires_in_days);
      await accessAPI.createCode(payload);
      setForm({ note: '', max_uses: 1, email: '', expires_in_days: '' });
      await refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not create the code'); }
    setBusy(false);
  };

  const revoke = async (code) => {
    try { await accessAPI.revokeCode(code); await refresh(); toast.success('Code revoked'); }
    catch { toast.error('Could not revoke'); }
  };

  const copyLink = async (code) => {
    const link = `${window.location.origin}/register?invite=${code}`;
    try { await navigator.clipboard.writeText(link); toast.success('Invite link copied'); } catch { toast.message(link); }
  };

  if (isLoading) return <p className="text-sm text-gray-500">Loading…</p>;
  const mode = data?.mode;

  return (
    <div className="space-y-6" data-testid="access-tab">
      <ChangePassword />
      <BrutalCard className="p-5">
        <h3 className="text-xl font-black uppercase mb-3 flex items-center gap-2"><KeyRound size={20} /> Who can sign up</h3>
        <div className="grid md:grid-cols-3 gap-3">
          {MODES.map((m) => (
            <button key={m.id} disabled={busy || mode === m.id}
              onClick={() => (m.id === 'open' ? setConfirmOpen(true) : setMode(m.id))}
              className={`text-left border-4 border-black p-3 ${mode === m.id ? 'bg-amber-200' : 'bg-white hover:bg-gray-50'}`}
              data-testid={`mode-${m.id}`}>
              <p className="font-black uppercase text-sm">{m.label}{mode === m.id ? ' (current)' : ''}</p>
              <p className="text-xs mt-1">{m.desc}</p>
            </button>
          ))}
        </div>
        {confirmOpen && (
          <div className="mt-4 border-4 border-black bg-rose-50 p-4" data-testid="open-confirm">
            <p className="text-sm font-bold mb-2">Opening sign-up makes Semantic Vision publicly available.</p>
            <label className="flex items-start gap-2 text-sm mb-3">
              <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-1" data-testid="open-ack" />
              <span>I understand, and the patent filings cover everything the product does today.</span>
            </label>
            <div className="flex gap-2">
              <BrutalButton variant="rose" size="sm" disabled={!ack || busy} onClick={() => setMode('open', true)} data-testid="open-confirm-btn">Open sign-up</BrutalButton>
              <BrutalButton variant="default" size="sm" onClick={() => { setConfirmOpen(false); setAck(false); }}>Cancel</BrutalButton>
            </div>
          </div>
        )}
      </BrutalCard>

      <BrutalCard className="p-5">
        <h3 className="text-xl font-black uppercase mb-3">Invite codes</h3>
        <form onSubmit={create} className="grid md:grid-cols-5 gap-3 items-end mb-4" data-testid="code-form">
          <label className="md:col-span-2 text-xs font-bold uppercase">Who is it for<input className={inputCls} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="e.g. Aunt May" data-testid="code-note" /></label>
          <label className="text-xs font-bold uppercase">Uses<input type="number" min="1" max="500" className={inputCls} value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} data-testid="code-uses" /></label>
          <label className="text-xs font-bold uppercase">Only this email (optional)<input type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
          <label className="text-xs font-bold uppercase">Expires in days<input type="number" min="1" max="365" className={inputCls} value={form.expires_in_days} onChange={(e) => setForm({ ...form, expires_in_days: e.target.value })} placeholder="never" /></label>
          <BrutalButton variant="amber" type="submit" disabled={busy} className="md:col-span-5" data-testid="code-create">Make an invite code</BrutalButton>
        </form>
        {data?.codes?.length === 0 ? (
          <p className="text-sm text-gray-500">No codes yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="code-table">
              <thead><tr className="text-left border-b-2 border-black"><th className="py-2 pr-3">Code</th><th className="pr-3">For</th><th className="pr-3">Used</th><th className="pr-3">Status</th><th className="pr-3">Joined</th><th /></tr></thead>
              <tbody>
                {data.codes.map((c) => (
                  <tr key={c.code} className="border-b border-gray-300 align-top">
                    <td className="py-2 pr-3 font-mono font-bold">{c.code}</td>
                    <td className="pr-3">{c.note || '—'}{c.email ? <div className="text-xs text-gray-500">{c.email}</div> : null}</td>
                    <td className="pr-3">{c.uses}/{c.max_uses}</td>
                    <td className="pr-3">{!c.active ? 'Revoked' : c.expired ? `Expired ${fmt(c.expires_at)}` : c.uses >= c.max_uses ? 'Used up' : c.expires_at ? `Until ${fmt(c.expires_at)}` : 'Active'}</td>
                    <td className="pr-3 text-xs">{c.used_by.map((u) => <div key={u.user_id}>{u.email} · {fmt(u.date)}</div>)}</td>
                    <td className="whitespace-nowrap">
                      <button onClick={() => copyLink(c.code)} className="mr-3 inline-flex items-center gap-1 underline" title="Copy invite link"><Copy size={14} /> Link</button>
                      {c.active && <button onClick={() => revoke(c.code)} className="underline text-rose-700" data-testid={`revoke-${c.code}`}>Revoke</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </BrutalCard>
    </div>
  );
};

export default AccessTab;
